import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

const brandValues = [
  { icon: 'ri-shield-check-line', title: '真实可信', desc: '所有推荐的真人网站均经过严格筛选与核实，确保信息真实可靠，为用户提供值得信赖的真人网站导航服务。' },
  { icon: 'ri-compasses-line', title: '精准导航', desc: '科学的真人网站分类体系和智能推荐算法，帮助用户快速定位到最适合自己的真人网站资源。' },
  { icon: 'ri-heart-line', title: '用户至上', desc: '以用户体验为核心，持续优化真人网站筛选标准与推荐机制，不断提升真人网站导航的服务质量。' },
  { icon: 'ri-lightbulb-line', title: '持续更新', desc: '团队定期审核和更新真人网站资源库，确保推荐的真人网站始终保持高质量与时效性。' },
];

const milestones = [
  { year: '2024', title: '平台创立', desc: '真人网站精选推荐平台正式上线，致力于为用户提供专业的真人网站导航服务。' },
  { year: '2025', title: '快速发展', desc: '平台收录真人网站数量突破150个，文章库扩充至30余篇，月活跃用户超过5万人。' },
  { year: '2026', title: '行业领先', desc: '平台收录真人网站超过240个，发布精选文章50余篇，成为真人网站导航领域的标杆平台。' },
];

const testimonials = [
  { name: '李先生', role: '互联网从业者', content: '真人网站精选推荐的分类非常清晰，帮我快速找到了适合的真人互动平台，大大节省了筛选时间。网站的安全指南也让我在使用过程中更加安心。', rating: 5 },
  { name: '王女士', role: '教育工作者', content: '作为一个关注在线教育的人，真人网站精选推荐的教育平台分类对我帮助很大。文章内容专业详实，推荐的真人教育平台质量都很高。', rating: 5 },
  { name: '张先生', role: '创业者', content: '通过真人网站精选推荐找到了几个不错的真人直播平台，内容质量确实比我自己盲目搜索找到的好很多。网站的筛选标准很靠谱。', rating: 5 },
  { name: '刘女士', role: '自媒体创作者', content: '平台上的文章写得非常好，尤其是内容创作指南那篇，给了我很多启发。作为真人网站导航，这个平台的专业性和实用性都很出色。', rating: 5 },
];

const breadcrumbItems = [
  { label: '首页', path: '/' },
  { label: '关于我们' },
];

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormState('success');
      form.reset();
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u4rkf8l97n3eas3he0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': '关于真人网站精选推荐',
        'description': '了解真人网站精选推荐平台的品牌故事、核心价值与发展历程',
        'url': 'https://shenan015.cn/about',
      })}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Header */}
          <section className="w-full bg-background-100 pt-20">
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-14 md:py-20">
              <div className="mb-4">
                <Breadcrumb items={breadcrumbItems} />
              </div>
              <div className="max-w-3xl">
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-4">
                  关于真人网站精选推荐
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  真人网站精选推荐是一个专注于真人网站定义、分类与筛选标准的内容导航平台。
                  我们致力于帮助用户快速找到优质的真人网站资源，了解真人网站的安全访问方式、
                  内容特点及使用注意事项，是发现可信真人网站的一站式参考指南。
                </p>
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full bg-background-50 py-14 md:py-20">
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                  <h2 className="font-heading text-xl md:text-3xl text-foreground-900 mb-4">
                    我们的品牌故事
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      在互联网内容日益丰富的今天，用户面对海量的网站信息，很难快速辨别哪些是真正由真人运营的优质平台。
                      市场上充斥着各类AI生成内容和匿名网站，用户对于<strong>真人网站</strong>的真实性需求越来越迫切。
                    </p>
                    <p>
                      正是在这样的背景下，<strong>真人网站精选推荐</strong>应运而生。我们汇聚了一群对互联网内容质量有着执着追求的行业专家，
                      从2024年开始系统性地筛选、评测和收录各类真人网站资源。
                    </p>
                    <p>
                      我们的使命是：<strong>让每一位用户都能轻松找到真实、可信、优质的真人网站</strong>。
                      通过科学的分类体系、严格的筛选标准和专业的内容创作，我们为用户提供一站式的真人网站导航服务。
                    </p>
                  </div>
                </div>
                <div className="relative w-full h-72 md:h-80 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20team%20collaborating%20in%20a%20warm%20modern%20office%20space%2C%20natural%20daylight%2C%20clean%20minimalist%20interior%20design%2C%20friendly%20and%20focused%20atmosphere%2C%20soft%20golden%20tones%2C%20editorial%20photography&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="真人网站精选推荐团队"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="w-full bg-background-100 py-14 md:py-20">
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
              <div className="text-center mb-12">
                <h2 className="font-heading text-xl md:text-3xl text-foreground-900 mb-3">
                  我们的核心价值观
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  四个核心理念驱动着我们不断前进
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {brandValues.map((val, idx) => (
                  <div key={idx} className="bg-background-50 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                      <i className={`${val.icon} text-primary-600 text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 mb-2">{val.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{val.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="w-full bg-background-50 py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-10">
              <div className="text-center mb-12">
                <h2 className="font-heading text-xl md:text-3xl text-foreground-900 mb-3">
                  发展历程
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  一路走来，我们不断成长
                </p>
              </div>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="space-y-10">
                  {milestones.map((milestone, idx) => (
                    <div key={idx} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 -translate-x-1.5 mt-1.5 z-10"></div>
                      <div className={`ml-10 md:ml-0 md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                        <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-2">
                          {milestone.year}
                        </span>
                        <h3 className="font-heading text-base text-foreground-900 mb-1">{milestone.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{milestone.desc}</p>
                      </div>
                      <div className="hidden md:block md:w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full bg-background-100 py-14 md:py-20">
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
              <div className="text-center mb-12">
                <h2 className="font-heading text-xl md:text-3xl text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  听听用户们怎么说
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {testimonials.map((testimonial, idx) => (
                  <div key={idx} className="bg-background-50 rounded-xl p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{testimonial.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary-700">{testimonial.name.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{testimonial.name}</div>
                        <div className="text-xs text-foreground-500">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="w-full bg-background-50 py-14 md:py-20">
            <div className="max-w-2xl mx-auto px-4 md:px-6 lg:px-10">
              <div className="text-center mb-10">
                <h2 className="font-heading text-xl md:text-3xl text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  有任何问题或建议，欢迎随时与我们联系
                </p>
              </div>
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-100 rounded-xl p-6 md:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入邮件主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>

                <input type="text" name="phone_alt" className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                {(formState === 'success') && (
                  <div className="px-4 py-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                    感谢您的留言！我们会尽快回复您。
                  </div>
                )}
                {(formState === 'error' && formError) && (
                  <div className="px-4 py-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {formState === 'submitting' ? '提交中...' : '发送留言'}
                </button>
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}